import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Dropdown,
  Icon,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  TooltipHost,
} from "@fluentui/react";
import { setNestedObjectValues, useFormik } from "formik";
import { t } from "i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import TabContainer from "../../components/TabContainer/TabContainer";
import CustomTab from "../../components/TabContainer/CustomTab";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import {
  GroupActiveSection,
  GroupConditionsSection,
  GroupDetailsSection,
} from "../../components/HangingProtocolV2/GroupEditor";
import { groupFormUrl } from "./HpV2GroupList";
import { kindLabel, kindOptions, toGroupKind, useStepStatus } from "./hpV2FormHelpers";
import { createHpV2GroupSchema } from "../../Schemas/HPV2Schema";
import { createFieldProps } from "../../helpers/hangingProtocol/hpFormFields";
import userContext from "../../context/users/UserContext";
import toastContext from "../../context/toast/ToastContext";
import { ACTIONS, AuthRoutes, TOAST_TYPE, USERTYPES } from "../../constants/Constants";
import { createGroup } from "../../helpers/hangingProtocol/hpDocument";
import { GROUP_KIND } from "../../helpers/hangingProtocol/protocolEnums";
import * as hpv2 from "../../service/HPV2Service";
import "./hangingProtocolV2.css";

/**
 * A hanging protocol V2 group: what it is called, what it covers and the
 * conditions that decide whether it applies at all.
 *
 * One step, so the wizard shell is here for the frame it gives the rest of the
 * platform's forms — breadcrumb, step panel, Cancel/Save — rather than for any
 * sequence to walk through. It was three: details and conditions were merged
 * because they edit one object and neither filled a screen, and the group's
 * protocols left for the list, where a group row expands to the protocols it
 * owns. Nothing about a protocol is reachable from here any more; adding,
 * removing and reordering them all live on that list.
 *
 * The envelope is a draft until Save.
 *
 * `kind` decides which keys the envelope may carry, so it is chosen once while
 * adding and shown as a chip afterwards — changing it later would mean rewriting
 * the group into a different collection. Only standard groups can be created for
 * now (`CREATABLE_KINDS`), which leaves nothing to choose, so the chip stands in
 * for the dropdown while adding too.
 */

const isOk = (response) => response?.status === 200 && response?.data?.result === "SUCCESS";

/** Stands in until the envelope has loaded; the step shows a spinner until then. */
const EMPTY_GROUP = {};

export default function HpV2GroupForm({ action = ACTIONS.EDIT }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { roles } = useContext(userContext);
  const { showToastv2 } = useContext(toastContext);

  const groupId = searchParams.get("id");
  const scope = searchParams.get("scope") ?? hpv2.HP_SCOPE.USER;

  const isAdmin = roles?.[0] === USERTYPES.ADMIN;
  // Two different reasons the fields are locked, and they are not interchangeable.
  // Viewing is the mode the list opens into and the Edit button is the way out of
  // it; the shared set belonging to an admin is a fact about this user, with no way
  // out and a bar at the top saying so.
  const lockedByScope = scope === hpv2.HP_SCOPE.SYSTEM && !isAdmin;
  const viewing = action === ACTIONS.VIEW;
  const readOnly = viewing || lockedByScope;
  const adding = action === ACTIONS.ADD;

  const [kind, setKind] = useState(hpv2.HP_KIND.STANDARD);
  // The envelope Formik starts from: what was loaded, or a blank one when adding.
  const [loaded, setLoaded] = useState(() => (adding ? createGroup({ name: "" }) : null));
  // The group as it is stored, so Cancel can put it back. Cancel lands on this
  // same screen in view mode, and the router hands the new action to the
  // component it already has rather than mounting a fresh one, so nothing else
  // would clear the edits that were just abandoned.
  const [baseline, setBaseline] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(!adding);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(null);

  const documentKind = toGroupKind(kind);
  const isStandard = documentKind === GROUP_KIND.STANDARD;

  /* ----------------------------------------------------------- validation */

  // Messages are baked in when the schema is built, so it is built once — as
  // v1's is. A language change remounts the screen.
  const schema = useMemo(() => createHpV2GroupSchema(t), []);

  /**
   * Formik holds the envelope so each field can carry its own error, the way the
   * rest of the platform's forms report themselves.
   */
  const formik = useFormik({
    initialValues: loaded ?? EMPTY_GROUP,
    enableReinitialize: true,
    validationSchema: schema,
    validateOnChange: true,
    validateOnBlur: true,
    // Saving is driven from the wizard's own Save button, which has to report a
    // failure as well as act on a pass — something Formik's submit gives no hook
    // for. See save().
    onSubmit: () => {},
  });

  const field = createFieldProps(formik);
  const draft = loaded ? formik.values : null;

  /* -------------------------------------------------------------- loading */

  const load = useCallback(async () => {
    if (adding || !groupId) return;
    setLoading(true);

    // The envelope carries the fields being edited but not the kind it was saved
    // under, which decides the key set — that is only on the summary list.
    const [summaries, envelope] = await Promise.all([
      hpv2.fetchGroups(scope),
      hpv2.fetchGroup(groupId, scope),
    ]);

    if (isOk(summaries)) {
      const summary = (summaries.data.data ?? []).find((group) => String(group.id) === String(groupId));
      setKind(summary?.kind ?? hpv2.HP_KIND.STANDARD);
    }
    if (isOk(envelope)) {
      setLoaded(envelope.data.data);
      setBaseline(envelope.data.data);
      setDirty(false);
      setError(null);
    } else {
      setError(envelope?.data?.message ?? t("hanging-protocol.load-failed"));
    }
    setLoading(false);
  }, [adding, groupId, scope]);

  useEffect(() => {
    load();
  }, [load]);

  /* --------------------------------------------------------------- edits */

  const onDraftChange = (next) => {
    formik.setValues(next);
    setDirty(true);
  };

  /** Switching kind while adding rebuilds the envelope: the key sets differ. */
  const onKindChange = (nextKind) => {
    setKind(nextKind);
    onDraftChange(
      createGroup({
        name: formik.values?.name ?? "",
        description: formik.values?.description ?? "",
        active: formik.values?.active !== false,
        kind: toGroupKind(nextKind),
      })
    );
  };

  /** Whether the one step has anything to report, for its status icon. */
  const stepInvalid = Boolean(formik.errors.name || formik.errors.description);

  const save = async () => {
    if (saving) return;
    setSubmitted(true);

    // Pressing Save is a claim about every field, including the ones never
    // visited — so mark each failing one touched and let it say so itself. The
    // one step is already on screen, so there is nowhere to navigate to.
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(setNestedObjectValues(errors, true), false);
      return;
    }

    setSaving(true);
    const response = await hpv2.saveGroup(formik.values, { scope, kind });
    setSaving(false);

    if (!isOk(response)) {
      // The server validates against the same key sets we do, so a failure here
      // is worth showing verbatim rather than a generic message.
      showToastv2(TOAST_TYPE.ERROR, response?.data?.message ?? t("hanging-protocol.save-failed"));
      return;
    }
    setDirty(false);
    setSubmitted(false);
    showToastv2(TOAST_TYPE.SUCCESS, t("hanging-protocol.saved"));
    if (adding) {
      // The group is all this screen holds, so a new one is finished here. The
      // next thing to do with it — give it protocols — is on the list.
      navigate(AuthRoutes.HANGINGPROTOCOLV2);
    } else {
      // Saving finishes the editing, so it closes it — the same place Cancel
      // lands, reached by committing rather than by abandoning: the group as
      // stored, read on the screen it was already on, with Edit the way back in.
      // The reload behind it re-reads what the server actually kept, which also
      // moves the baseline forward so the next Cancel falls back to this version
      // rather than to the one it replaced.
      load();
      navigate(groupFormUrl(ACTIONS.VIEW, { id: groupId, scope }));
    }
  };

  /** Drops the draft and goes, whether or not the destination remounts us. */
  const leave = (url) => {
    setDirty(false);
    setSubmitted(false);
    // Clears what was reported along with what was typed: coming back to a
    // group that was abandoned mid-edit must not still be red about it.
    if (baseline) formik.resetForm({ values: baseline });
    navigate(url);
  };

  /** Leaving this draft behind asks first. */
  const leaveTo = (url) => {
    if (dirty) {
      setConfirmLeave(url);
      return;
    }
    leave(url);
  };

  const backToList = () => leaveTo(AuthRoutes.HANGINGPROTOCOLV2);

  /**
   * Cancel undoes the editing, not the visit: it drops the edits and shows the
   * group as it is stored, on the screen it was already on — Edit is the way
   * back in and the list is a breadcrumb away. A group being added has nothing
   * stored to fall back to, so that one is still a way out to the list.
   */
  const cancel = () =>
    leaveTo(
      adding || !groupId
        ? AuthRoutes.HANGINGPROTOCOLV2
        : groupFormUrl(ACTIONS.VIEW, { id: groupId, scope })
    );

  /* -------------------------------------------------------------- render */

  const { status } = useStepStatus({ adding, readOnly, submitted });

  /** Every step body sits inside the tab container's own scroll area. */
  const step = (children) => (
    <div className="hpv2-step">
      {loading ? <Spinner label={t("hanging-protocol.loading")} /> : <div className="hpv2-editor">{children}</div>}
    </div>
  );

  /* Kind decides the key set, so it is chosen once while adding and is a chip
     from then on: a read-only field spent a whole row saying "Standard". While
     standard is the only creatable kind there is nothing to choose at all, so
     adding shows the chip as well — the dropdown returns with the second kind. */
  const choosingKind = adding && kindOptions().length > 1;

  const kindBadge = (
    <TooltipHost content={t("hanging-protocol.type-note")}>
      <span className="hpv2-editor-kind">
        {kindLabel(kind)}
        <Icon iconName="Info" />
      </span>
    </TooltipHost>
  );

  const setupStep = step(
    <>
      <GroupDetailsSection
        group={draft}
        kind={documentKind}
        disabled={readOnly}
        onChange={onDraftChange}
        field={field}
        badge={choosingKind ? null : kindBadge}
        kindControl={
          choosingKind ? (
            <Dropdown
              label={t("hanging-protocol.type")}
              selectedKey={kind}
              options={kindOptions()}
              disabled={readOnly}
              onChange={(_e, option) => onKindChange(option.key)}
            />
          ) : null
        }
      />

      {/* Special kinds carry no groupConditions at all — not merely disabled. */}
      {isStandard && (
        <GroupConditionsSection group={draft} disabled={readOnly} onChange={onDraftChange} />
      )}

      {/* Switches off the whole group, so it closes the step rather than
          sitting among the fields it would disable. */}
      <GroupActiveSection group={draft} disabled={readOnly} onChange={onDraftChange} />
    </>
  );

  // One step, whichever kind and whether adding or editing.
  const tabDetails = [
    {
      selectedKey: "1",
      tabName: t("hanging-protocol.group-setup"),
      tabKeyContain: (
        <CustomTab
          orderNumber="1"
          tabName={t("hanging-protocol.group-setup")}
          // A special kind has neither devices nor conditions, so the standard
          // subtitle would promise fields that are not on the step.
          subtitle={t(
            isStandard
              ? "sub-title.hanging-protocol.group-setup"
              : "sub-title.hanging-protocol.group-setup-special"
          )}
          iconName={status("setup", stepInvalid).value}
          iconColor={status("setup", stepInvalid).color}
          action={readOnly ? ACTIONS.VIEW : action}
        />
      ),
      tabValueContain: setupStep,
    },
  ];

  /* The way out of view mode, and the only button the header carries there. Not
     offered on the shared set to someone who could not save it anyway — that lock
     has no key, and the bar at the top of the screen is what says so. */
  const topButtons = [
    {
      key: "edit",
      button: (
        <PrimaryButton
          id="edit-hpv2-group"
          style={{ marginLeft: "10px" }}
          text={t("button.edit")}
          onClick={() => navigate(groupFormUrl(ACTIONS.EDIT, { id: groupId, scope }))}
        />
      ),
    },
  ];

  // onClick rather than href, so leaving runs the unsaved-changes guard.
  const breadCrumbItems = [
    {
      text: t("hanging-protocol.list"),
      key: "hpv2-list",
      onClick: (event) => {
        event?.preventDefault();
        backToList();
      },
    },
    {
      text: adding ? t("hanging-protocol.add-group-button") : draft?.name || t("hanging-protocol.group"),
      key: "hpv2-group",
    },
  ];

  return (
    <>
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}
      {/* Only the lock with no key: view mode is not news, it has an Edit button. */}
      {lockedByScope && (
        <MessageBar messageBarType={MessageBarType.info}>{t("hanging-protocol.read-only")}</MessageBar>
      )}

      <TabContainer
        uniqueKey={"hpv2group_"}
        tabDetails={tabDetails}
        NoOfTabs={tabDetails.length}
        defaultSelectedValue="1"
        // Nowhere to go next or back from the only step, so the arrows would be
        // two permanently dead controls.
        previousNextButton={false}
        action={readOnly ? ACTIONS.VIEW : action}
        onCancelClick={cancel}
        onSubmitClick={save}
        topButtons={viewing && !lockedByScope ? topButtons : []}
        breadCrumbItems={breadCrumbItems}
      />

      {confirmLeave && (
        <ConfirmModal
          showConfirmModal
          title={t("hanging-protocol.unsaved-title")}
          message={t("hanging-protocol.unsaved-body")}
          confirmButtonText={t("hanging-protocol.discard")}
          handleConfirm={() => {
            const url = confirmLeave;
            setConfirmLeave(null);
            leave(url);
          }}
          handleNotConfirm={() => setConfirmLeave(null)}
        />
      )}
    </>
  );
}
